import React, { useState, useEffect } from "react";
import {
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Eye,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Types
export interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  type?: "text" | "number" | "date" | "actions";
  formatter?: (value: any, row: any) => React.ReactNode;
  width?: string;
  nowrap?: boolean;
}

export interface Action {
  icon: "edit" | "delete" | "view";
  label: string;
  onClick: (row: any) => void;
  className?: string;
}

export interface DataTableProps {
  data: any[];
  columns: Column[];
  actions?: Action[];
  searchable?: boolean;
  paginated?: boolean;
  entriesPerPage?: number[];
  loading?: boolean;
  totalCount: number;
  currentPage?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  searchValue?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  onSort?: (column: string, order: "asc" | "desc") => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (row: any) => void;
  className?: string;
  emptyMessage?: string;
  zebra?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  data = [],
  columns = [],
  actions = [],
  searchable = true,
  paginated = true,
  entriesPerPage = [10, 25, 50, 100],
  loading = false,
  totalCount,
  currentPage = 1,
  pageSize = 10,
  sortBy,
  sortOrder,
  searchValue = "",
  onSearch,
  searchPlaceholder = "Rechercher...",
  onSort,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  className,
  emptyMessage = "Aucune donnée disponible",
  zebra = true,
}) => {
  const [internalSearchValue, setInternalSearchValue] = useState(searchValue);
  const [internalSortBy, setInternalSortBy] = useState(sortBy);
  const [internalSortOrder, setInternalSortOrder] = useState<"asc" | "desc">(
    sortOrder || "asc"
  );

  // Update internal state when external props change
  useEffect(() => {
    setInternalSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    setInternalSortBy(sortBy);
    setInternalSortOrder(sortOrder || "asc");
  }, [sortBy, sortOrder]);

  // Format different data types
  const formatValue = (value: any, type: string = "text") => {
    if (value === null || value === undefined) return "-";

    switch (type) {
      case "number":
        return typeof value === "number"
          ? value.toLocaleString("fr-FR")
          : value;

      case "date": {
        if (!value) return "-";
        const date = new Date(value);
        return new Intl.DateTimeFormat("fr-FR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
      }

      default:
        return value;
    }
  };

  // Handle sorting
  const handleSort = (column: Column) => {
    if (!column.sortable) return;

    let newOrder: "asc" | "desc" = "asc";
    if (internalSortBy === column.key && internalSortOrder === "asc") {
      newOrder = "desc";
    }

    setInternalSortBy(column.key);
    setInternalSortOrder(newOrder);

    if (onSort) {
      onSort(column.key, newOrder);
    }
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearchValue(value);

    if (onSearch) {
      onSearch(value);
    }
  };

  // Get action icon
  const getActionIcon = (iconType: string) => {
    switch (iconType) {
      case "edit":
        return <Pencil size={16} />;
      case "delete":
        return <Trash2 size={16} />;
      case "view":
        return <Eye size={16} />;
      default:
        return null;
    }
  };

  // Calculate pagination info
  const startEntry = totalCount ? (currentPage - 1) * pageSize + 1 : 0;
  const endEntry = totalCount
    ? Math.min(currentPage * pageSize, totalCount)
    : data.length;
  const totalPages = totalCount
    ? Math.ceil(totalCount / pageSize)
    : Math.ceil(data.length / pageSize);

  // Render table rows
  const renderTableRows = () => {
    if (loading) {
      return Array.from({ length: pageSize }).map((_, index) => (
        <tr key={index}>
          {columns.map((column) => (
            <td key={column.key} className="px-4 py-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            </td>
          ))}
          {actions.length > 0 && (
            <td className="px-4 py-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
            </td>
          )}
        </tr>
      ));
    }

    if (data.length === 0) {
      return (
        <tr>
          <td
            colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
            className="px-4 py-8 text-center text-gray-500"
          >
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return data.map((row, index) => (
      <tr
        key={row.id || index}
        className={cn(
          "transition-colors",
          zebra && index % 2 === 0
            ? "bg-white"
            : zebra
            ? "bg-gray-50/50"
            : "bg-white",
          onRowClick ? "cursor-pointer hover:bg-primary/5" : "hover:bg-gray-50"
        )}
        onClick={(e) => {
          // Don't trigger row click if clicking on action buttons
          const target = e.target as HTMLElement;
          if (
            !target.closest("button") &&
            !target.closest("[data-no-row-click]") &&
            onRowClick
          ) {
            onRowClick(row);
          }
        }}
      >
        {columns.map((column) => (
          <td
            key={column.key}
            className={cn(
              "px-4 py-4 text-sm text-gray-900",
              column.nowrap && "whitespace-nowrap"
            )}
          >
            {column.type === "actions"
              ? null
              : column.formatter
              ? column.formatter(row[column.key], row)
              : formatValue(row[column.key], column.type)}
          </td>
        ))}
        {actions.length > 0 && (
          <td className="px-4 py-4 text-sm" data-no-row-click>
            <div className="flex items-center gap-2">
              {actions.map((action, actionIndex) => (
                <button
                  key={actionIndex}
                  onClick={() => action.onClick(row)}
                  className={cn(
                    "p-1 rounded hover:bg-gray-100 transition-colors",
                    action.className
                  )}
                  title={action.label}
                >
                  {getActionIcon(action.icon)}
                </button>
              ))}
            </div>
          </td>
        )}
      </tr>
    ));
  };

  return (
    <div className={cn("bg-white rounded-lg shadow-sm border", className)}>
      {/* Header with entries selector and search */}
      {(paginated || searchable) && (
        <div
          className={cn(
            "p-4 border-b flex flex-col sm:flex-row items-start sm:items-center gap-4",
            paginated ? "justify-between" : "justify-end"
          )}
        >
          {paginated && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Afficher</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => onPageSizeChange?.(Number(value))}
              >
                <SelectTrigger className="w-[70px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {entriesPerPage.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">entrées</span>
            </div>
          )}

          {searchable && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={internalSearchValue}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                    column.sortable &&
                      "cursor-pointer hover:bg-gray-100 select-none",
                    column.width && `w-${column.width}`
                  )}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <div className="flex flex-col ml-2">
                        <ChevronUp
                          size={12}
                          className={cn(
                            "text-gray-400",
                            internalSortBy === column.key &&
                              internalSortOrder === "asc"
                              ? "text-primary"
                              : "text-gray-300"
                          )}
                        />
                        <ChevronDown
                          size={12}
                          className={cn(
                            "text-gray-400 -mt-1",
                            internalSortBy === column.key &&
                              internalSortOrder === "desc"
                              ? "text-primary"
                              : "text-gray-300"
                          )}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {renderTableRows()}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginated && totalCount > 0 && (
        <div className="px-4 py-3 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-700">
            Affichage de <span className="font-medium">{startEntry}</span> à{" "}
            <span className="font-medium">{endEntry}</span> sur{" "}
            <span className="font-medium">{totalCount}</span> entrées
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-primary/5 hover:border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Précédent
            </button>

            <div className="flex items-center gap-1">
              {(() => {
                const pageNumbers = [];

                if (totalPages <= 5) {
                  // Show all pages if 5 or less
                  for (let i = 1; i <= totalPages; i++) {
                    pageNumbers.push(i);
                  }
                } else {
                  // Always show first page
                  pageNumbers.push(1);

                  if (currentPage > 3) {
                    pageNumbers.push("...");
                  }

                  // Show pages around current page
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);

                  for (let i = start; i <= end; i++) {
                    pageNumbers.push(i);
                  }

                  if (currentPage < totalPages - 2) {
                    pageNumbers.push("...");
                  }

                  // Always show last page
                  pageNumbers.push(totalPages);
                }

                return pageNumbers.map((pageNum, index) => {
                  if (pageNum === "...") {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 text-gray-500"
                      >
                        ...
                      </span>
                    );
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange?.(pageNum as number)}
                      className={cn(
                        "px-3 py-1 text-sm border rounded-md",
                        currentPage === pageNum
                          ? "bg-primary text-white border-primary"
                          : "border-gray-300 hover:bg-primary/5 hover:border-primary/20 transition-colors"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                });
              })()}
            </div>

            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-primary/5 hover:border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
